import Plotly from "plotly.js-dist-min";

import { renderGrowl } from "@js/nwcc-growl.js";
import { getStates, getEasternRegions, getWesternRegions } from "@js/nwcc-navbar.js";

import { populateElements, setBookmarkTextBox } from "../controller/home.js";
import * as getData from "../model/get-data.js";
import { getBookmarkLink } from "./home.js";

const defaultNetworkColors = makeNetworkColorMap(getData.getValidNetworks());
export const filterMapId = "filter-map";

export function clearFilterOptions() {
    const nullOption = /*html*/ `<option value="" selected="selected"></option>`;
    document.getElementById("select--region").innerHTML = nullOption;
    document.getElementById("select--state").innerHTML = nullOption;
}

export function getSelectedNetworks() {
    const selectedNetworks = [];
    const networksCb = document.querySelectorAll('[name="cb--networks"]');
    networksCb.forEach(function (networkListItem) {
        if (networkListItem.checked) {
            const selectedNetwork = networkListItem.id.split("--")[1].toUpperCase();
            selectedNetworks.push(selectedNetwork);
        }
    });
    return selectedNetworks;
}

export function getFilteredStations(stateOverride, regionOverride, networksOverride) {
    const filteredMeta = [];
    const stateFilter = stateOverride || document.getElementById("select--state")?.value;
    const regionFilter = regionOverride || document.getElementById("select--region")?.value;
    const networkFilters = networksOverride || getSelectedNetworks();
    const meta = getData.getMeta();
    meta.forEach(function (stationMeta) {
        const huc = stationMeta.huc?.toUpperCase();
        const state = stationMeta.stateCode?.toUpperCase();
        const network = stationMeta.networkCode?.toUpperCase();
        let inNetwork = false;
        let inState = false;
        let inRegion = false;
        inNetwork = networkFilters.includes(network);
        if (stateFilter) {
            inState = state ? state === stateFilter : false;
        } else {
            inState = true;
        }
        if (regionFilter) {
            inRegion = huc ? huc.startsWith(regionFilter) : false;
        } else {
            inRegion = true;
        }
        if (inNetwork && inState && inRegion) {
            filteredMeta.push(stationMeta);
        }
    });
    if (filteredMeta.length === 0) {
        const msg = "The current network/state/region filter combination results in no stations.";
        renderGrowl("Adjust the filters!", msg, "error", true, 10);
    }
    return filteredMeta;
}

export async function populateFilters() {
    clearFilterOptions();
    const states = getStates();
    const filteredMeta = getFilteredStations();
    const metaStates = [...new Set(filteredMeta.map((stationMeta) => stationMeta.stateCode.toUpperCase()))];
    const stateFilter = document.getElementById("select--state");
    states.forEach(function (state) {
        if (metaStates.includes(state.abbr.toUpperCase())) {
            const option = document.createElement("option");
            option.value = state.abbr;
            option.innerHTML = state.name;
            stateFilter.appendChild(option);
        }
    });

    const metaRegions = [
        ...new Set(
            filteredMeta.map((stationMeta) => {
                if (stationMeta.huc) {
                    return stationMeta.huc.slice(0, 2);
                }
            }),
        ),
    ];
    const regionFilter = document.getElementById("select--region");

    const westernRegionOptions = [];
    const westernRegions = getWesternRegions();
    westernRegions.forEach(function (region) {
        if (metaRegions.includes(region.abbr)) {
            const option = document.createElement("option");
            option.value = region.abbr;
            option.innerHTML = `${region.name} (${region.abbr})`;
            westernRegionOptions.push(option);
        }
    });
    if (westernRegionOptions.length) {
        const westernRegionGrp = document.createElement("optgroup");
        westernRegionGrp.label = "Western Regions";
        regionFilter.appendChild(westernRegionGrp);
        westernRegionOptions.forEach((option) => {
            westernRegionGrp.appendChild(option);
        });
    }

    const easternRegionOptions = [];
    const easternRegions = getEasternRegions();
    easternRegions.forEach(function (region) {
        if (metaRegions.includes(region.abbr)) {
            const option = document.createElement("option");
            option.value = region.abbr;
            option.innerHTML = `${region.name} (${region.abbr})`;
            easternRegionOptions.push(option);
        }
    });
    if (easternRegionOptions.length) {
        const easternRegionGrp = document.createElement("optgroup");
        easternRegionGrp.label = "Eastern Regions";
        regionFilter.appendChild(easternRegionGrp);
        easternRegionOptions.forEach((option) => {
            easternRegionGrp.appendChild(option);
        });
    }
}

export function addFilterListeners() {
    const filterTypes = ["state", "region"];
    filterTypes.forEach(function (filterType) {
        const filter = document.getElementById(`select--${filterType}`);
        filter?.addEventListener("change", async () => {
            await filterChange(filterType, false);
        });
    });
    document.getElementById("btn-clear--region")?.addEventListener("click", async (event) => {
        await filterChange("region", true);
    });
    document.getElementById("btn-clear--state")?.addEventListener("click", async (event) => {
        await filterChange("state", true);
    });
}

export async function filterChange(filterType, clearFilter = false) {
    const filterTypes = ["state", "region"];
    if (filterTypes.includes(filterType.toLowerCase())) {
        const thisFilter = document.getElementById(`select--${filterType}`);
        if (clearFilter) {
            thisFilter.value = "";
        }
        await updateStations();
        await updateFilterMap();
        const otherFilterType = filterTypes.indexOf(filterType) === 0 ? filterTypes[1] : filterTypes[0];
        const otherFilter = document.getElementById(`select--${otherFilterType}`);
        if ((otherFilter.value !== "" && clearFilter) || !clearFilter) {
            await zoomToMiddle();
        }
        getBookmarkLink();
    }
}

export function clearMetaDropDowns(metaType = "all") {
    const nullOption = /*html*/ `<option value="" selected="selected"></option>`;
    const stationSelect = document.getElementById("select--station");
    const tbSelectedStation = document.getElementById("tb--station");
    const elementSelect = document.getElementById("select--element");
    const durationSelect = document.getElementById("select--duration");
    const metaSelects = [stationSelect, elementSelect, durationSelect];
    metaSelects
        .filter((select) => select)
        .forEach(function (select) {
            if (metaType.toLowerCase() === "all") {
                select.innerHTML = nullOption;
                tbSelectedStation.value = "";
            } else {
                if (select?.id.split("--")[1].toLowerCase() === metaType.toLowerCase()) {
                    select.innerHTML = nullOption;
                }
                if (metaType === "station") {
                    tbSelectedStation.value = "";
                }
            }
        });
}

export async function stationSelectionChange(stationOverride) {
    const station = stationOverride || document.getElementById("select--station")?.value;
    if (station) {
        await getData.updateSelectedStationMeta();
        getData.clearGlobalSelectedStationData("data");
        clearMetaDropDowns("duration");
        clearMetaDropDowns("element");
        populateElements();
        zoomToSelectedStation();
    } else {
        clearMetaDropDowns("duration");
        clearMetaDropDowns("element");
        getData.clearGlobalSelectedStationData();
    }
}

export async function updateStations(stationOverride) {
    const stationSelect = document.getElementById("select--station");
    const currentStationSelectTriplet = stationOverride || stationSelect.value || "";
    const filteredMeta = getFilteredStations();
    const filteredTriplets = filteredMeta.map((stationMeta) => stationMeta.stationTriplet);
    clearMetaDropDowns();
    filteredMeta.forEach(function (stationMeta) {
        const option = document.createElement("option");
        option.value = stationMeta.stationTriplet;
        option.innerHTML = `${stationMeta.name} (${stationMeta.stationTriplet})`;
        stationSelect.appendChild(option);
    });
    if (filteredTriplets.includes(currentStationSelectTriplet)) {
        stationSelect.value = currentStationSelectTriplet;
        const selectedStationMeta = getData.getSelectedStationMeta();
        const selectedStationLabel = `${selectedStationMeta.name} (${selectedStationMeta.stationTriplet})`;
        document.getElementById("tb--station").value = selectedStationLabel;
    }
}

export function getFigData() {
    const selectedStationMeta = getData.getSelectedStationMeta();
    const stationsMeta = getFilteredStations();
    const selectedStationTriplet = selectedStationMeta.stationTriplet || "NA";
    const selectedStationNetwork = selectedStationMeta.networkCode || "NA";
    const networks = getSelectedNetworks();
    const networkLookUp = getData.getRefData().networks;
    const data = [];
    networks.forEach(function (network) {
        const networkMeta = stationsMeta.filter((stationMeta) => stationMeta.networkCode === network);
        const networkLongitudes = [];
        const networkLatitudes = [];
        const networkStationLabels = [];
        const networkStationTriplets = [];
        const networkStationMarkerSizes = [];
        const networkStationMarkerColors = [];
        networkMeta.forEach(function (stationMeta) {
            const name = stationMeta.name;
            const trip = stationMeta.stationTriplet;
            const elev = stationMeta.elevation;
            const lat = stationMeta.latitude;
            const long = stationMeta.longitude;
            const beginDate = stationMeta.beginDate?.split(" ")[0] || "Unknown";
            networkLongitudes.push(long || NaN);
            networkLatitudes.push(lat || NaN);
            networkStationTriplets.push(trip || "");
            const stationLabel =
                name && trip && elev && beginDate
                    ? /*html*/ `${name}<br>ID: ${trip}<br>Elevation: ${elev}'<br>Since: ${beginDate}`
                    : "";
            networkStationLabels.push(stationLabel);
            const isSelectedStation = trip === selectedStationMeta.stationTriplet;
            const markerSize = isSelectedStation ? getMarkerSize(true) : getMarkerSize(false);
            networkStationMarkerSizes.push(markerSize);
            const markerColor = isSelectedStation ? getNetworkColor("selected") : getNetworkColor(network);
            networkStationMarkerColors.push(markerColor);
        });
        let networkCount = networkMeta.length;

        const networkName = networkLookUp[network]?.name || "Unknown Network";
        let trace = {
            type: "scattermap",
            lon: networkLongitudes,
            lat: networkLatitudes,
            text: networkStationLabels,
            name: `${networkName} (${networkCount})`,
            mode: "markers",
            hoverinfo: "text",
            stationTriplet: networkStationTriplets,
            legendgrouptitle: { text: "Displayed Networks" },
            legendgroup: "stations",
            legendrank: data.length + 1,
            network: network,
            marker: {
                size: networkStationMarkerSizes,
                color: networkStationMarkerColors,
            },
        };
        if (network === selectedStationNetwork) {
            const selectedSiteIndex = networkStationTriplets.indexOf(selectedStationTriplet);
            trace = sendSiteToTopLayer(trace, selectedSiteIndex);
            data.push(trace);
        } else {
            data.unshift(trace);
        }
    });
    return data;
}

export async function updateFilterMap() {
    const layout = getFilterMapLayout();
    const overlays = getFilterMapOverlays();
    layout.map.layers = overlays;
    const selectedStationShape = getSelectedStationLegendEntry();
    layout.shapes = selectedStationShape;
    const options = getFilterMapOptions();
    const data = getFigData();
    const figData = data.length === 0 ? [{ type: "scattermap" }] : data;
    await Plotly.react(filterMapId, figData, layout, options);
}

export function makeNetworkColorMap(networks) {
    const networkColors = {};
    const colorList = [
        "rgba(35,120,195,0.85)",
        "rgba(165, 55, 253, 0.85)",
        "rgba(127,177,53,0.85)",
        "rgba(230,199,76,0.85)",
    ];
    networks.forEach(function (network, idx) {
        networkColors[network.toLowerCase()] = colorList[idx % 4];
    });
    networkColors.selected = "rgba(216,57,51,1)";
    return networkColors;
}

export function getNetworkColor(network) {
    return defaultNetworkColors[network.toLowerCase()] || "black";
}

export function getMarkerSize(isSelected) {
    const markerSize = isSelected ? 30 : 15;
    return markerSize;
}

export function getFilterMapOverlays(stateOveride, regionOveride) {
    const overlays = [];

    const state = stateOveride ? stateOveride.toUpperCase() : document.getElementById("select--state")?.value;
    if (state) {
        const filteredFeatures =
            getData.getStateOverlay().features.filter((feature) => feature.properties.abbr === state) || [];
        const filterStateOverlay = {
            source: {
                type: "FeatureCollection",
                features: filteredFeatures,
            },
            type: "fill",
            color: "rgba(100,0,0,0.05)",
        };
        if (filteredFeatures.length > 0) {
            overlays.push(filterStateOverlay);
        }
    }

    const region = regionOveride ? regionOveride.toUpperCase() : document.getElementById("select--region")?.value;
    if (region) {
        const filteredFeatures =
            getData.getRegionOverlay().features.filter((feature) => feature.properties.huc2 === region) || [];
        const filterRegionOverlay = {
            source: {
                type: "FeatureCollection",
                features: filteredFeatures,
            },
            type: "fill",
            color: "rgba(0,0,100,0.05)",
        };
        if (filteredFeatures.length > 0) {
            overlays.push(filterRegionOverlay);
        }
    }
    return overlays;
}

export function findMiddle(arr) {
    arr.sort((a, b) => a - b);
    const middleIndex = Math.floor(arr.length / 2);
    if (arr.length % 2 === 0) {
        return (arr[middleIndex - 1] + arr[middleIndex]) / 2;
    } else {
        return arr[middleIndex];
    }
}

export function getFilterMapOptions() {
    const options = {
        displaylogo: false,
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
        responsive: true,
    };
    return options;
}

export function getZoneExtents(zone) {
    const zoneLookup = {
        WEST: { zoom: 3.3, center: [41.4, -113.8] },
        EAST: { zoom: 2.75, center: [35.7, -83.8] },
        AK: { zoom: 2.6, center: [64.2, -151.1] },
        HI: { zoom: 5.5, center: [20.4, -157.9] },
    };
    const zoneObj = zoneLookup[zone];
    return { zoom: zoneObj.zoom, center: { lat: zoneObj.center[0], lon: zoneObj.center[1] } };
}

export async function installFilterMapViewControls() {
    const zones = ["WEST", "EAST", "AK", "HI"];
    zones.forEach((zone) => {
        const btnId = `btn--zoom-${zone.toLowerCase()}`;
        const viewBtn = document.getElementById(btnId);
        viewBtn?.addEventListener("click", async () => {
            await zoomToZone(zone);
        });
    });
    const localZoomBtn = document.getElementById("btn--zoom-local");
    localZoomBtn.addEventListener("click", zoomToLocal);
    const zoomToStationBtn = document.getElementById("btn--zoom-station");
    zoomToStationBtn.addEventListener("click", async () => {
        await zoomToStationButtonClick();
    });
}

export async function zoomToStationButtonClick() {
    if (document.getElementById("tb--station")?.value) {
        await zoomToSelectedStation();
    } else {
        renderGrowl("Select a station!", "Must select a station to use this function.", "warning", true, 10);
    }
}

export async function zoomToZone(zone) {
    const selectedZone = getZoneExtents(zone);
    const layoutZoomUpdate = {
        map: {
            center: selectedZone.center,
            zoom: selectedZone.zoom,
            style: getMapStyle(),
            layers: getFilterMapOverlays(),
        },
    };
    await Plotly.relayout(filterMapId, layoutZoomUpdate);
}

export async function zoomToMiddle() {
    const stationsMeta = getFilteredStations();
    if (stationsMeta.length > 1) {
        const lats = [];
        const longs = [];
        stationsMeta.forEach((station) => {
            lats.push(station.latitude);
            longs.push(station.longitude);
        });
        const maxBound = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...longs) - Math.min(...longs)) * 111;
        const zoom = 10.7 - Math.log(maxBound);
        const layoutZoomUpdate = {
            map: {
                center: {
                    lon: findMiddle(longs),
                    lat: findMiddle(lats),
                },
                zoom: +zoom.toFixed(3),
                style: getMapStyle(),
                layers: getFilterMapOverlays(),
            },
        };
        await Plotly.relayout(filterMapId, layoutZoomUpdate);
    }
}

export function getSelectedStationLegendEntry() {
    const meta = getData.getSelectedStationMeta();
    if (Object.keys(meta).length > 0) {
        const networkLookUp = getData.getRefData().networks;
        return [
            {
                type: "circle",
                xref: "paper",
                yref: "paper",
                x0: 0,
                y0: 0,
                x1: 0,
                y1: 0,
                visible: true,
                line: { width: 0 },
                fillcolor: getNetworkColor("selected"),
                showlegend: true,
                name: meta.name,
                legendrank: 0,
                legendgroup: "selectedStation",
                layer: "below",
                legendgrouptitle: {
                    text: `Selected ${networkLookUp[meta.networkCode].name}`,
                },
            },
        ];
    } else {
        return [];
    }
}

export function sendSiteToTopLayer(networkStationsTrace, indexOfSite) {
    function sendToBack(data, idx) {
        if (idx !== -1) {
            const updatedData = [...data];
            updatedData.push(...updatedData.splice(idx, 1));
            return updatedData;
        }
        return data;
    }
    Object.keys(networkStationsTrace).forEach((stationKey) => {
        if (Array.isArray(networkStationsTrace[stationKey])) {
            networkStationsTrace[stationKey] = sendToBack(networkStationsTrace[stationKey], indexOfSite);
        }
    });
    if (networkStationsTrace.marker) {
        networkStationsTrace.marker = sendSiteToTopLayer(networkStationsTrace.marker, indexOfSite);
    }
    return networkStationsTrace;
}

export async function zoomToSelectedStation(figDataOverride) {
    const selectedStationMarkerSizeUpdate = {};
    const layoutZoomUpdate = {};
    const selectedStationMeta = getData.getSelectedStationMeta();
    const figData = figDataOverride || document.getElementById(filterMapId)?.data;
    if (figData && Object.keys(selectedStationMeta).length > 0) {
        const selectedStationNetwork = selectedStationMeta.networkCode;
        const selectedStationTriplet = selectedStationMeta.stationTriplet;
        let networkTraceIndex = [];
        const resetMarkerSizeIndexes = [];
        figData.forEach((networkTrace, idx) => {
            if (selectedStationNetwork === networkTrace.network) {
                networkTraceIndex = [idx];
            } else {
                resetMarkerSizeIndexes.push(idx);
            }
        });
        const selectedStationLabel = `${selectedStationMeta.name} (${selectedStationTriplet})`;
        document.getElementById("tb--station").value = selectedStationLabel;
        const stationMarkerSizes = [];
        const stationMarkerColors = [];
        const selectedSiteIndex = figData[networkTraceIndex]?.stationTriplet?.indexOf(selectedStationTriplet) || 0;
        figData[networkTraceIndex] = sendSiteToTopLayer(figData[networkTraceIndex], selectedSiteIndex);
        figData[networkTraceIndex].stationTriplet.forEach((trip) => {
            const isSelectedStation = trip === selectedStationMeta.stationTriplet;
            const markerSize = isSelectedStation ? getMarkerSize(true) : getMarkerSize(false);
            stationMarkerSizes.push(markerSize);
            const markerColor = isSelectedStation
                ? getNetworkColor("selected")
                : getNetworkColor(selectedStationNetwork);
            stationMarkerColors.push(markerColor);
        });
        selectedStationMarkerSizeUpdate.marker = {
            size: stationMarkerSizes,
            color: stationMarkerColors,
        };

        const center = {
            lon: selectedStationMeta.longitude,
            lat: selectedStationMeta.latitude,
        };
        const zoom = 8;
        const shapes = getSelectedStationLegendEntry();
        layoutZoomUpdate.map = {
            center: center,
            zoom: zoom,
            style: getMapStyle(),
            layers: getFilterMapOverlays(),
        };
        layoutZoomUpdate.shapes = shapes;

        await Plotly.update(filterMapId, selectedStationMarkerSizeUpdate, layoutZoomUpdate, networkTraceIndex);
        if (resetMarkerSizeIndexes.length > 0) {
            resetMarkerSizeIndexes.forEach(async function (markerIdx) {
                const markerNetwork = figData[markerIdx].network;
                const resetMarkerSizeUpdate = {
                    marker: {
                        size: 15,
                        color: getNetworkColor(markerNetwork),
                    },
                };
                await Plotly.restyle(filterMapId, resetMarkerSizeUpdate, markerIdx);
            });
        }
    }
}

export async function zoomToLocal() {
    const localZoomBtn = document.getElementById("btn--zoom-local");
    async function success(position) {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        const layoutZoomUpdate = {
            map: {
                center: { lat: latitude, lon: longitude },
                zoom: 9,
                style: getMapStyle(),
                layers: getFilterMapOverlays(),
            },
        };
        await Plotly.relayout(filterMapId, layoutZoomUpdate);
    }
    function error() {
        const msg = "Unable to get geolocation, check your browser settings.";
        renderGrowl("Could not get location!", msg, "warning", true);
        localZoomBtn.focus();
    }
    if (!navigator.geolocation) {
        const msg = "Your browser does not support geolocation, this functionality is not available.";
        renderGrowl("No browser support!", msg, "error", true);
        localZoomBtn.focus();
    } else {
        navigator.geolocation.getCurrentPosition(success, error);
    }
}

export function getFilterMapLayout() {
    const filterMap = document.getElementById(filterMapId);
    let zoom = filterMap?.layout?.map?.zoom || getZoneExtents("WEST").zoom;
    let center = filterMap?.layout?.map?.center || getZoneExtents("WEST").center;
    const layout = {
        autosize: true,
        margin: { b: 0, t: 0, l: 0, r: 0, pad: 0 },
        showlegend: true,
        legend: {
            orientation: "v",
            xanchor: "left",
            yanchor: "top",
            x: 0,
            y: 1,
            bgcolor: "rgba(255,255,255,0.85)",
            tracetoggle: false,
        },
        map: {
            center: center,
            zoom: zoom,
            layers: [],
            style: getMapStyle(),
        },
        geo: {
            projection: {
                type: "orthographic",
            },
        },
    };
    return layout;
}

export async function loadFilterMap() {
    await Plotly.newPlot(filterMapId, []);
    const filterMap = document.getElementById(filterMapId);
    const isPlotlyFig = filterMap.classList.contains("js-plotly-plot");
    if (isPlotlyFig) {
        filterMap.on("plotly_click", async function (clickData) {
            await stationClick(clickData);
        });
    }
    await installFilterMapViewControls();
    await updateFilterMap();
    if (document.getElementById("select--region")?.value || document.getElementById("select--state")?.value) {
        zoomToMiddle();
    }
}

export async function stationClick(clickData) {
    if (clickData.points.length === 1) {
        const stationTriplets = clickData.points[0].data.stationTriplet;
        const clickIndex = clickData.points[0].pointIndex;
        document.getElementById("select--station").value = stationTriplets[clickIndex];
        await stationSelectionChange();
        await setBookmarkTextBox();
    }
}

export function getMapStyle() {
    return {
        id: "osm",
        version: 8,
        sources: {
            "street-map": {
                type: "raster",
                tiles: [
                    "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
                    "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
                ],
                tileSize: 256,
            },
        },
        layers: [
            {
                id: "street-map",
                type: "raster",
                source: "street-map",
            },
        ],
    };
}
